// D:\directiveai-react\agthealth\agthealth-frontend\src\components\Login\views\ForgotPasswordView.js

import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Step,
  StepLabel,
  Autocomplete, 
  FormHelperText, 
  InputAdornment,
  Alert,
  Snackbar
} from '@mui/material';
import { motion } from 'framer-motion';
import { ChevronRight, ChevronLeft, Search, Lock, CheckCircle } from 'lucide-react';
import { 
  RegisterCard, 
  ActionButton, 
  BackButton, 
  StyledTextField, 
  AuthCard 
} from '../styles/StyledComponents';
import { validateRUT, validateSSN } from '../utils/validators';
import { HEALTH_GREEN } from '../utils/mockData';
import AppleSpinner from '../../../common/AppleSpinner';
import CountrySelector from '../components/CountrySelector';
import StepperWrapper from '../components/StepperWrapper';
import CredentialCapture from '../components/CredentialCapture';
import PasswordFields from '../components/PasswordFields';
import UserTypeSelector from '../components/UserTypeSelector';
import useCamera from '../hooks/useCamera';
import agthealthService from '../../../services/agthealthService';

const ForgotPasswordView = ({ onBackToLogin, isMobile, userType, onUserTypeChange }) => {
  // Forgot password states
  const [forgotPasswordStep, setForgotPasswordStep] = useState(0);
  const [forgotPasswordCountry, setForgotPasswordCountry] = useState('');
  const [forgotPasswordId, setForgotPasswordId] = useState('');
  const [forgotPasswordIdError, setForgotPasswordIdError] = useState('');
  const [forgotPasswordHealthCenter, setForgotPasswordHealthCenter] = useState(null);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCenters, setFilteredCenters] = useState([]);
  const [healthCenters, setHealthCenters] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'error' });
  
  // Password states
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Credential verification states
  const [credentialVerified, setCredentialVerified] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  
  // Use camera hook
  const cameraHook = useCamera();

  // Get forgot password steps
  const getForgotPasswordSteps = () => {
    return isMobile
      ? ['Country', 'ID', 'Email', 'Verify', 'Reset']
      : ['Country', 'Identification', 'Email', 'Credential Verification', 'Reset Password'];
  };

  const forgotPasswordSteps = getForgotPasswordSteps();

  // Load health centers when country changes
  useEffect(() => {
    if (forgotPasswordCountry) {
      loadHealthCenters(forgotPasswordCountry);
    }
  }, [forgotPasswordCountry]);

  // Load health centers from API
  const loadHealthCenters = async (countryCode) => {
    try {
      const response = await agthealthService.getHealthCentersByCountry(countryCode);
      if (response.success && response.data) {
        setHealthCenters(response.data);
      }
    } catch (error) {
      console.error('Error loading health centers:', error);
      setAlert({
        open: true,
        message: 'Error al cargar centros de salud',
        severity: 'error'
      });
    }
  };

  // Filter health centers when search term changes
  useEffect(() => {
    if (forgotPasswordCountry && searchTerm) {
      const centers = healthCenters || [];
      const filtered = centers.filter(center => 
        center.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCenters(filtered);
    }
  }, [forgotPasswordCountry, searchTerm, healthCenters]);

  // Validate email format
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Start verification process
  const startVerification = async () => {
    setVerificationLoading(true);
    
    try {
      // Request password reset
      const response = await agthealthService.forgotPassword({
        email,
        countryCode: forgotPasswordCountry,
        identificationNumber: forgotPasswordId
      });
      
      if (response.success) {
        setCredentialVerified(true);
        
        // In development, we get the token directly
        if (response.data?.resetToken) {
          setResetToken(response.data.resetToken);
        }
        
        // Move to password reset step
        setForgotPasswordStep(4);
      } else {
        throw new Error(response.message || 'Error requesting password reset');
      }
    } catch (error) {
      console.error('Error requesting password reset:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Error al solicitar restablecimiento de contraseña',
        severity: 'error'
      });
    } finally {
      setVerificationLoading(false);
    }
  };

  // Handle password changes
  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
    validatePasswords(e.target.value, confirmPassword);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    validatePasswords(newPassword, e.target.value);
  };

  const validatePasswords = (newPwd, confirmPwd) => {
    if (confirmPwd && newPwd !== confirmPwd) {
      setPasswordError('Passwords do not match');
      return false;
    } else if (newPwd && newPwd.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return false;
    } else {
      setPasswordError('');
      return true;
    }
  };

  // Forgot password navigation
  const handleForgotPasswordNext = () => {
    // Validate before proceeding
    if (forgotPasswordStep === 1) {
      if (forgotPasswordCountry === 'cl' && !validateRUT(forgotPasswordId)) {
        setForgotPasswordIdError('Invalid RUT format. Example: 12345678-9');
        return;
      } else if (forgotPasswordCountry === 'us' && !validateSSN(forgotPasswordId)) {
        setForgotPasswordIdError('Invalid format. Use SSN (XXX-XX-XXXX)');
        return;
      }
      setForgotPasswordIdError('');
    }
    
    // For step 2 (Email), validate email
    if (forgotPasswordStep === 2) {
      if (!email) {
        setEmailError('Email is required');
        return;
      }
      
      if (!validateEmail(email)) {
        setEmailError('Please enter a valid email address');
        return;
      }
      
      setEmailError('');
    }
    
    // For step 3 (Verification), start verification process
    if (forgotPasswordStep === 3) {
      startVerification();
      return;
    }
    
    // For final step (Password Reset), validate passwords
    if (forgotPasswordStep === 4) {
      if (!validatePasswords(newPassword, confirmPassword)) {
        return;
      }
      handleForgotPasswordComplete();
      return;
    }
    
    setForgotPasswordStep((prev) => prev + 1);
  };

  const handleForgotPasswordBack = () => {
    if (forgotPasswordStep === 0) {
      onBackToLogin();
    } else {
      setForgotPasswordStep((prev) => prev - 1);
    }
  };

  const handleForgotPasswordComplete = async () => {
    setIsSubmitting(true);
    
    try {
      // Reset password with token
      const response = await agthealthService.resetPassword({
        token: resetToken,
        newPassword
      });
      
      if (response.success) {
        setForgotPasswordSuccess(true);
      } else {
        throw new Error(response.message || 'Error resetting password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Error al restablecer contraseña',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine if the current forgot password step is complete
  const isForgotPasswordStepComplete = () => {
    switch (forgotPasswordStep) {
      case 0: return !!forgotPasswordCountry;
      case 1: return !!forgotPasswordId && !forgotPasswordIdError;
      case 2: return !!email && !emailError;
      case 3: return true; // Always allow proceeding to verification
      case 4: return newPassword && confirmPassword && !passwordError && newPassword.length >= 8;
      default: return false;
    }
  };

  // Forgot password step content
  const getForgotPasswordStepContent = () => {
    switch (forgotPasswordStep) {
      case 0:
        return (
          <CountrySelector 
            selectedCountry={forgotPasswordCountry} 
            onSelectCountry={setForgotPasswordCountry} 
          />
        );
      
      case 1:
        return (
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {forgotPasswordCountry === 'cl' 
                ? 'Please enter your RUT (without dots and with dash)' 
                : 'Please enter your Social Security Number'}
            </Typography>
            <StyledTextField
              fullWidth
              label={forgotPasswordCountry === 'cl' ? 'RUT' : 'SSN'}
              variant="outlined"
              value={forgotPasswordId}
              onChange={(e) => {
                setForgotPasswordId(e.target.value);
                setForgotPasswordIdError(''); // Clear error on change
              }}
              placeholder={forgotPasswordCountry === 'cl' ? '12345678-9' : '123-45-6789'}
              error={!!forgotPasswordIdError}
              helperText={forgotPasswordIdError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{ width: 24, height: 16, marginRight: 0 }}>
                      <Box 
                        component="img" 
                        src={`https://flagcdn.com/w40/${forgotPasswordCountry}.png`}
                        alt={`${forgotPasswordCountry === 'cl' ? 'Chile' : 'US'} flag`}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {forgotPasswordCountry === 'cl' 
                ? 'Example: 12345678-9' 
                : 'Example: 123-45-6789'}
            </Typography>
          </Box>
        );
      
      case 2:
        return (
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Enter your email address
            </Typography>
            <StyledTextField
              fullWidth
              label="Email"
              variant="outlined"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError('');
              }}
              error={!!emailError}
              helperText={emailError}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              We'll send a password reset link to this email
            </Typography>
          </Box>
        );
      
      case 3:
        return (
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Verify your identity
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              We need to verify your identity before resetting your password.
              Click continue to proceed with verification.
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Lock size={64} color={HEALTH_GREEN} />
              </motion.div>
            </Box>
          </Box>
        );
      
      case 4:
        return (
          <PasswordFields
            newPassword={newPassword}
            confirmPassword={confirmPassword}
            passwordError={passwordError}
            onNewPasswordChange={handleNewPasswordChange}
            onConfirmPasswordChange={handleConfirmPasswordChange}
            isMobile={isMobile}
          />
        );
      
      default:
        return 'Unknown step';
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  if (forgotPasswordSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: '100%' }}
      >
        <AuthCard>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            textAlign: 'center',
            my: 4
          }}>
            <CheckCircle size={isMobile ? 64 : 80} color={HEALTH_GREEN} />
            <Typography variant={isMobile ? "h5" : "h4"} sx={{ mt: 3, fontWeight: 600 }}>
              Password Reset Complete
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Your password has been successfully reset.
              You can now log in with your new password.
            </Typography>
            
            <ActionButton 
              variant="contained"
              disableElevation
              fullWidth
              onClick={onBackToLogin}
              sx={{ mt: 4 }}
            >
              Return to Login
            </ActionButton>
          </Box>
        </AuthCard>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ width: '100%' }}
    >
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
      
      <RegisterCard>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          mb: isMobile ? 2 : 3, 
          alignItems: 'center'
        }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            sx={{ 
              fontWeight: 600, 
              mb: 1, 
              display: 'flex', 
              alignItems: 'center' 
            }}
          >
            AGT Health
            <motion.span
              initial={{ scale: 1 }}
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1, repeat: Infinity, repeatDelay: 5 }}
              style={{ 
                color: HEALTH_GREEN, 
                fontSize: isMobile ? '24px' : '32px', 
                fontWeight: 700, 
                marginLeft: '2px',
                position: 'relative',
                top: '-8px'
              }}
            >
              +
            </motion.span>
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Lock size={isMobile ? 18 : 20} color={HEALTH_GREEN} />
            <Typography variant="subtitle1" color="text.secondary">
              Reset Your Password
            </Typography>
          </Box>
        </Box>

        <UserTypeSelector 
          userType={userType} 
          onUserTypeChange={onUserTypeChange} 
        />

        <StepperWrapper 
          steps={forgotPasswordSteps}
          activeStep={forgotPasswordStep}
        />

        {getForgotPasswordStepContent()}

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <BackButton
            startIcon={<ChevronLeft size={16} />}
            onClick={handleForgotPasswordBack}
            disabled={isSubmitting}
            fullWidth={isMobile}
          >
            {forgotPasswordStep === 0 ? 'Back to Login' : 'Back'}
          </BackButton>
          
          {forgotPasswordStep === 4 ? (
            <ActionButton 
              variant="contained"
              disableElevation
              disabled={!isForgotPasswordStepComplete() || isSubmitting}
              onClick={handleForgotPasswordComplete}
              fullWidth={isMobile}
            >
              {isSubmitting ? <AppleSpinner sizeDesktop={24} sizeMobile={20} /> : 'Reset Password'}
            </ActionButton>
          ) : (
            <ActionButton 
              variant="contained"
              disableElevation
              disabled={!isForgotPasswordStepComplete()}
              onClick={handleForgotPasswordNext}
              fullWidth={isMobile}
              endIcon={<ChevronRight size={16} />}
            >
              Continue
            </ActionButton>
          )}
        </Box>
      </RegisterCard>
    </motion.div>
  );
};

export default ForgotPasswordView;